import { z } from 'zod'

// Schema for different content types (same as blocks-schema)
const videoContentSchema = z.object({
  duration: z.number(),
  videoUrl: z.string(),
  resources: z.array(z.string()).optional(),
  transcript: z.string().optional(),
})

const textContentSchema = z.object({
  html: z.string(),
  wordCount: z.number(),
  attachments: z.array(z.object({
    url: z.string(),
    name: z.string(),
    type: z.string(),
  })).optional(),
  readingTime: z.number(),
})

const assignmentContentSchema = z.object({
  hints: z.array(z.string()).optional(),
  instructions: z.string(),
  expectedOutput: z.string().optional(),
})

const imageContentSchema = z.object({
  alt: z.string(),
  caption: z.string(),
  imageUrl: z.string(),
  resources: z.array(z.string()).optional(),
})

const downloadContentSchema = z.object({
  files: z.array(z.object({
    url: z.string(),
    name: z.string(),
    size: z.string(),
    type: z.string(),
  })),
  instructions: z.string(),
})

const documentContentSchema = z.object({
  documentUrl: z.string(),
  fileName: z.string().optional(),
  fileSize: z.string().optional(),
  fileType: z.string().optional(),
})

// Union type for different content types
const blockContentSchema = z.union([
  videoContentSchema,
  textContentSchema,
  assignmentContentSchema,
  imageContentSchema,
  downloadContentSchema,
  documentContentSchema,
  z.record(z.string(), z.any()) // Fallback for unknown content types
])

// Schema for block
const blockSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  type: z.enum(['VIDEO', 'TEXT', 'ASSIGNMENT', 'IMAGE', 'DOWNLOAD', 'DOCUMENT']),
  content: blockContentSchema,
  order: z.number(),
  duration: z.number(),
  isPublished: z.boolean(),
  lessonId: z.string(),
  creatorId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
})

// Schema for course (minimal, nested in module)
const courseInfoSchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
})

// Schema for quiz (minimal, nested in module)
const quizSchema = z.object({
  id: z.string(),
  title: z.string(),
  passingScore: z.number(),
  maxAttempts: z.number(),
  isPublished: z.boolean(),
  // Optional progress field (only if authenticated)
  isPassed: z.boolean().optional(),
})

// Schema for module (nested in lesson)
const moduleInfoSchema = z.object({
  id: z.string(),
  title: z.string(),
  course: courseInfoSchema,
  quiz: quizSchema.nullable(),
})

// Schema for creator
const creatorSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatar: z.string().nullable(),
})

// Schema for access details
const accessDetailsSchema = z.object({
  isEnrolled: z.boolean(),
  isCertificateObtained: z.boolean(),
  isAccessExpired: z.boolean(),
  isAccessBlocked: z.boolean(),
  enrollmentStatus: z.string(),
  courseStatus: z.string(),
  courseIsPublished: z.boolean(),
  userStatus: z.string(),
  moduleId: z.string(),
  moduleTitle: z.string(),
  previousModuleCompleted: z.boolean().optional(),
  lessonId: z.string(),
  lessonTitle: z.string(),
  previousLessonCompleted: z.boolean().optional(),
  previousLessonId: z.string().optional(),
  previousLessonTitle: z.string().optional(),
})

// Schema for access
const accessSchema = z.object({
  hasAccess: z.boolean(),
  reason: z.string().optional(),
  code: z.string().optional(),
  details: accessDetailsSchema,
})

// Schema for lesson content
const lessonContentSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  order: z.number(),
  duration: z.number(),
  isPublished: z.boolean(),
  moduleId: z.string(),
  creatorId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  module: moduleInfoSchema,
  creator: creatorSchema,
  blocks: z.array(blockSchema),
  access: accessSchema,
})

// Schema for the lesson content API response (after apiClient extracts data.data)
export const lessonContentApiResponseSchema = lessonContentSchema

// Schema for the complete API response (before apiClient processing)
export const fullLessonContentApiResponseSchema = z.object({
  success: z.boolean(),
  data: lessonContentSchema,
  messages: z.array(z.string()).optional(),
  errors: z.array(z.string()).optional(),
})

export type LessonContentApiResponse = z.infer<typeof lessonContentApiResponseSchema>
export type LessonContent = z.infer<typeof lessonContentSchema>
export type BlockContent = z.infer<typeof blockSchema>
export type AccessInfo = z.infer<typeof accessSchema>
export type AccessDetails = z.infer<typeof accessDetailsSchema>

